import React from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
  alpha,
  Chip,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  DashboardOutlined,
  AccountBalanceWalletOutlined,
  ReceiptLongOutlined,
  ShowChartOutlined,
  CalculateOutlined,
  HandymanOutlined,
  RequestQuoteOutlined,
  CreditCardOutlined,
  ShieldOutlined,
  InsightsOutlined,
  PersonOutlineOutlined,
  LogoutOutlined,
  OpenInNew,
  AdminPanelSettingsOutlined,
  BlockOutlined,
} from '@mui/icons-material';
import { useSupabase } from '../contexts/SupabaseContext';
import { useAccess } from '../contexts/AccessContext';
import { moduleForPath } from '../config/modules';

const SIDEBAR_WIDTH = 248;

// Public menu items (visible without login)
const publicMenuItems = [
  { text: 'Finance Calculator', icon: <CalculateOutlined />, path: '/finance-calculator', external: true, module: 'finance-calculator' },
];

// Private menu items (require login), grouped into sidebar sections
const privateMenuSections = [
  {
    title: 'Overview',
    items: [
      { text: 'Dashboard', icon: <DashboardOutlined />, path: '/' },
      { text: 'Analysis', icon: <InsightsOutlined />, path: '/analysis', module: 'analysis' },
    ],
  },
  {
    title: 'Money',
    items: [
      { text: 'Transactions', icon: <ReceiptLongOutlined />, path: '/transactions', module: 'transactions' },
      { text: 'Wallets', icon: <AccountBalanceWalletOutlined />, path: '/wallets', module: 'wallets' },
    ],
  },
  {
    title: 'Wealth',
    items: [
      { text: 'Investments', icon: <ShowChartOutlined />, path: '/investments', module: 'investments' },
    ],
  },
  {
    title: 'Protection',
    items: [
      { text: 'Insurance', icon: <ShieldOutlined />, path: '/insurance', module: 'insurance' },
    ],
  },
  {
    title: 'Liabilities',
    items: [
      { text: 'Loans', icon: <RequestQuoteOutlined />, path: '/loans', module: 'loans' },
      { text: 'Credit Cards', icon: <CreditCardOutlined />, path: '/credit-cards', module: 'credit-cards' },
    ],
  },
  {
    title: 'Tools',
    items: [
      { text: 'Utilities', icon: <HandymanOutlined />, path: '/utilities', module: 'utilities' },
      { text: 'Finance Calculator', icon: <CalculateOutlined />, path: '/finance-calculator', external: true, module: 'finance-calculator' },
    ],
  },
  {
    title: 'Account',
    items: [
      { text: 'Profile Update', icon: <PersonOutlineOutlined />, path: '/profile-update' },
    ],
  },
];

const isActivePath = (pathname, path) =>
  path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);

const Brand = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <Box component="img" src="/icon-192x192.png" alt="" sx={{ width: 30, height: 30, borderRadius: 1.75 }} />
    <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.02em', color: 'text.primary' }}>
      Mickles
    </Typography>
  </Box>
);

const Layout = () => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { signOut, user } = useSupabase();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { isAdmin, isEnabled } = useAccess();

  // Sidebar sections based on login status. Modules switched off by an admin are hidden
  // (admins still see them, marked "Off"); admins get an extra Admin section.
  const baseSections = user ? privateMenuSections : [{ title: 'Tools', items: publicMenuItems }];
  const menuSections = [
    ...baseSections
      .map(section => ({ ...section, items: section.items.filter(item => isAdmin || isEnabled(item.module)) }))
      .filter(section => section.items.length),
    ...(user && isAdmin ? [{ title: 'Admin', items: [{ text: 'Admin', icon: <AdminPanelSettingsOutlined />, path: '/admin' }] }] : []),
  ];

  // The page's module is switched off: users see a notice, admins see the page with a banner
  const currentModule = moduleForPath(pathname);
  const moduleOff = currentModule && !isEnabled(currentModule.key);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleNavigate = (item) => {
    if (item.external) {
      window.open(item.path, '_blank');
    } else {
      navigate(item.path);
    }
    setDrawerOpen(false);
  };

  const userEmail = user?.email || '';
  const userName = user?.user_metadata?.full_name || userEmail.split('@')[0];

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ height: 64, px: 2.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Brand />
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        {menuSections.map((section) => (
          <Box key={section.title} sx={{ mt: 2 }}>
            <Typography
              variant="overline"
              sx={{ px: 1.5, color: 'text.disabled', fontSize: '0.6875rem', lineHeight: 2 }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => {
                const active = !item.external && isActivePath(pathname, item.path);
                return (
                  <ListItemButton
                    key={item.text}
                    selected={active}
                    onClick={() => handleNavigate(item)}
                    sx={{
                      mb: 0.25,
                      py: 0.875,
                      px: 1.5,
                      color: active ? 'primary.main' : 'text.secondary',
                      '& .MuiListItemIcon-root': { color: active ? 'primary.main' : 'text.secondary' },
                      '&:hover': { color: 'text.primary', '& .MuiListItemIcon-root': { color: 'text.primary' } },
                      '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                      '&.Mui-selected:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, '& svg': { fontSize: 20 } }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 500 }}
                    />
                    {item.module && !isEnabled(item.module) && (
                      <Chip label="Off" size="small" sx={{ height: 18, fontSize: '0.625rem', mr: item.external ? 0.5 : 0 }} />
                    )}
                    {item.external && <OpenInNew sx={{ fontSize: 14, opacity: 0.5 }} />}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>
      {user && (
        <>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 34, height: 34, fontSize: '0.875rem', fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }}>
              {(userName || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{userName}</Typography>
              <Typography variant="caption" noWrap component="div" color="text.secondary">{userEmail}</Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          ml: { md: `${SIDEBAR_WIDTH}px` },
          zIndex: (t) => (isDesktop ? t.zIndex.appBar : t.zIndex.drawer + 1),
          bgcolor: alpha(theme.palette.background.paper, 0.85),
          backdropFilter: 'saturate(180%) blur(8px)',
        }}
      >
        <Toolbar sx={{ gap: 1.5, minHeight: { xs: 56, sm: 64 } }}>
          {!isDesktop && (
            <>
              <IconButton edge="start" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
                <MenuIcon />
              </IconButton>
              <Brand />
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />

          {user ? (
            <Button variant="outlined" size="small" onClick={handleSignOut} startIcon={<LogoutOutlined fontSize="small" />}>
              Logout
            </Button>
          ) : (
            <Button variant="contained" size="small" component={Link} to="/login">
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Desktop: permanent sidebar. Phones/tablets: slide-out drawer. */}
      <Box component="nav" sx={{ width: { md: SIDEBAR_WIDTH }, flexShrink: { md: 0 } }}>
        {isDesktop ? (
          <Drawer
            variant="permanent"
            open
            sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, boxSizing: 'border-box', bgcolor: 'background.paper' } }}
          >
            {sidebarContent}
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: { xs: '85%', sm: 280 }, maxWidth: 320, boxSizing: 'border-box' } }}
          >
            {sidebarContent}
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // minWidth 0 lets this flex child shrink to the screen instead of
          // growing to fit wide tables/charts (which caused sideways scrolling)
          minWidth: 0,
          width: '100%',
          p: { xs: 1, sm: 3, md: 2 },
          pb: { xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 3 },
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} /> {/* Spacer matching the AppBar height */}
        <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
          {moduleOff && isAdmin && (
            <Alert severity="warning" sx={{ mx: { xs: 1, sm: 3 }, mt: { xs: 1, sm: 2 } }}>
              {currentModule.name} is switched off for users. You can see it because you're an admin (Admin → Modules).
            </Alert>
          )}
          {moduleOff && !isAdmin ? (
            <Box sx={{ textAlign: 'center', py: 10, px: 3 }}>
              <BlockOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>{currentModule.name} is not available</Typography>
              <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                This part of Mickles has been switched off for now. Your data is safe and will be here when it's back.
              </Typography>
              <Button variant="contained" onClick={() => navigate(user ? '/' : '/login')}>{user ? 'Go to Dashboard' : 'Sign in'}</Button>
            </Box>
          ) : (
            <Outlet />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
